<template>
  <b-card
    v-if="shipment && declaration"
    title="Customs declaration"
  >
    <b-row v-if="isPostValidationAmendment">
      <b-col
        lg="3"
        md="4"
      >
        <b-form-group
          label="Bank account number"
          label-for="bank-account"
        >
          <validation-provider
            #default="{ errors }"
            name="Bank account number"
            rules="required|min:7|max:35"
          >
            <b-form-input
              id="bank-account"
              v-model="declaration.communication.bank_account"
              :disabled="disabled"
              class="mr-0 ml-0"
              :state="errors.length > 0 ? false:null"
            />
          </validation-provider>
        </b-form-group>
      </b-col>
      <b-col
        lg="6"
        md="6"
      >
        <label>
          Documents
        </label>
        <b-form-file
          v-if="!upload.isUploading"
          id="upload-btn"
          :ref="`fileHandle-${declaration.id}`"
          v-model="uploadFile"
          placeholder="Select a file"
          drop-placeholder="Drop file here..."
          :multiple="true"
          :state="declaration.communication.customs_attachments.length === 0 ? false:null"
          class="b-custom-control-xl"
          @input="submitFile()"
        />
        <b-progress
          v-if="upload.isUploading"
          ref="fileProgress"
          :value="upload.uploadProgress"
          max="100"
        />
      </b-col>
    </b-row>
    <b-row v-if="isPostValidationAmendment && declaration.communication.files">
      <b-col
        lg="6"
        md="6"
      />
      <b-col
        lg="6"
        md="6"
      >
        <label>Documents</label><br>
        <div
          v-for="(file, idx) in declaration.communication.files"
          :key="idx"
          style="display: inline;margin-right: 10px;"
        >
          <b-link :href="`${fileBaseUrl}/files/${file.id}`">
            {{ file.name }}
          </b-link>
        </div>
      </b-col>
    </b-row>
    <b-row v-if="declaration.declaration_type !== 'ENS'">
      <b-col
        v-if="declaration.previous_document_category"
        lg="3"
        md="4"
      >
        <label>Previous document category</label>
        <v-select
          v-model="declaration.previous_document_category"
          :options="previousDocumentCategories"
          :get-option-label="formatOption"
          :clearable="false"
          :disabled="disabled"
          :reduce="option => option.code"
          input-id="previous-document-category"
        />
      </b-col>
      <b-col
        lg="3"
        md="4"
      >
        <label for="previous-document-type-input">Previous document type</label>
        <classifier-select
          id="previous-document-type"
          v-model="declaration.previous_document_type"
          :classifier-key="classifierKeys.previousDocumentTypes"
          :country="previousDocumentCountry"
          :empty-code-selectable="previousDocumentCountry === 'NL'"
          :clearable="false"
          :disabled="disabled"
        />
      </b-col>
      <b-col
        lg="3"
        md="4"
      >
        <label>Previous document reference</label>
        <b-form-input
          id="previous-document-reference"
          v-model="declaration.previous_document_reference"
          :disabled="disabled"
          class="mr-0 ml-0"
        />
      </b-col>
      <b-col
        lg="3"
        md="4"
      >
        <b-form-group
          label="Choose warehouse"
          label-for="vue-select"
        >
          <validation-provider
            #default="{ errors }"
            name="Warehouse"
            :rules="'required'"
          >
            <b-form-select
              id="warehouse"
              v-model="declaration.warehouse_id"
              :options="warehouses"
              :state="errors.length > 0 ? false : null"
              :disabled="disabled"
            />
          </validation-provider>
        </b-form-group>
      </b-col>
      <b-col
        v-if="requiresNumberOfItems"
        lg="3"
        md="4"
      >
        <label>Previous document number of items</label>
        <b-form-input
          id="previous-document-number-of-items"
          v-model="declaration.previous_document_number_of_items"
          :disabled="disabled"
          class="mr-0 ml-0"
        />
      </b-col>
      <b-col
        v-if="declaration.declaration_type === 'H1'"
        lg="3"
        md="4"
      >
        <b-form-group
          label="Inland mode of transport"
          label-for="inland_mode_of_transport"
        >
          <validation-provider
            #default="{ errors }"
            name="Inland mode of transport"
            :rules="'required'"
          >
            <classifier-select
              id="inland_mode_of_transport"
              v-model="declaration.inland_mode_of_transport"
              :classifier-key="classifierKeys.transportModes"
              :country="classifierCountry"
              :state="errors.length > 0 ? false : null"
              :disabled="disabled"
            />
          </validation-provider>
        </b-form-group>
      </b-col>
      <b-col
        v-if="declaration.declaration_type === 'H1'"
        lg="3"
        md="4"
      >
        <b-form-group
          label="Mode of transport at border"
          label-for="mode_of_transport_at_border"
        >
          <validation-provider
            #default="{ errors }"
            name="Mode of transport at border"
            :rules="'required'"
          >
            <classifier-select
              id="mode_of_transport_at_border"
              v-model="declaration.mode_of_transport_at_border"
              :classifier-key="classifierKeys.transportModes"
              :country="classifierCountry"
              :state="errors.length > 0 ? false : null"
              :disabled="disabled"
            />
          </validation-provider>
        </b-form-group>
      </b-col>
      <b-col
        v-if="declaration.declaration_type === 'H1'"
        lg="3"
        md="4"
      >
        <label>Transport registration number</label>
        <b-form-input
          id="transport-registration-number"
          v-model="declaration.transport_registration_number"
          :disabled="disabled"
          class="mr-0 ml-0"
        />
      </b-col>
    </b-row>
    <b-row v-if="declaration.status_info && declaration.status_info['errors']">
      <b-col>
        <label v-if="errors.length">Declaration status</label>
        <ul class="pl-2">
          <li
            v-for="(error, index) in errors"
            :key="index"
          >
            {{ error.message || 'error message is undefined' }}
          </li>
        </ul>
      </b-col>
    </b-row>
  </b-card>
</template>

<script>
import {
  BRow,
  BCol,
  BFormInput,
  BCard, BFormGroup, BFormSelect, BProgress, BFormFile, BLink,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import { ValidationProvider } from 'vee-validate'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import ClassifierSelect from '@/views/components/ClassifierSelect.vue'
import {
  CUSTOMS_CLASSIFIER_KEYS,
  PREVIOUS_DOCUMENT_CATEGORIES,
  classifierCountryParam,
  formatClassifierOption,
  getPreviousDocumentTypes,
} from '@/libs/customsClassifiers'

export default {
  components: {
    ClassifierSelect,
    BLink,
    BFormFile,
    BProgress,
    BFormSelect,
    ValidationProvider,
    BFormGroup,
    BCard,
    vSelect,
    BRow,
    BCol,
    BFormInput,
  },
  props: {
    shipment: {
      type: Object,
      required: true,
    },
    declaration: {
      type: Object,
      required: true,
    },
    parcel: {
      type: Object,
      required: true,
    },
    disabled: {
      type: Boolean,
    },
  },
  data() {
    return {
      // The shared X/Y/Z constant, rendered by the shared formatter so the
      // category reads as code plus description like every classifier list.
      previousDocumentCategories: PREVIOUS_DOCUMENT_CATEGORIES,
      // Canonical classifier options: { code, label, parentCode, attributes, flags }.
      // Loaded from the shared classifier client, which caches them, so this
      // costs no request beyond the one the dropdown above already makes.
      previousDocumentTypes: [],
      classifierKeys: CUSTOMS_CLASSIFIER_KEYS,
      warehouses: [{ value: '', text: '----select----' }],
      upload: {
        isUploading: false,
        uploadProgress: 0,
      },
      uploadFile: null,
      fileBaseUrl: `${process.env.VUE_APP_API_URL}/v1/companies/${this.shipment.company_id}/shipments/${this.shipment.id}/declarations/${this.declaration.id}`,
    }
  },
  computed: {
    requiresNumberOfItems() {
      const selectedOption = this.previousDocumentTypes
        .find(option => option.code === this.declaration.previous_document_type)
      return selectedOption?.flags?.require_number_of_items ?? false
    },
    errors() {
      if (this.declaration.declaration_type === 'ENS') {
        return this.parcel?.status_info?.errors ?? []
      }
      return this.declaration?.status_info?.errors ?? []
    },
    isPostValidationAmendment() {
      return !this.disabled
        && this.declaration
        && ['LV', 'DK'].includes(this.declaration.customs_country)
        && ['cleared', 'cleared_manually'].includes(this.declaration.status)
    },
    // Transport-mode validation is country-aware on the backend, so the selects
    // pass the entry country and let the backend select its snapshot.
    classifierCountry() {
      return classifierCountryParam(this.shipment.entry_country)
    },
    // Every country resolves its own previous document type list; the backend
    // serves the country overlay or the curated list for it.
    previousDocumentCountry() {
      return classifierCountryParam(this.shipment.entry_country)
    },
  },
  watch: {
    previousDocumentCountry() {
      this.previousDocumentTypes = []
      this.loadPreviousDocumentTypes()
    },
    'declaration.previous_document_type': function onPreviousDocumentType() {
      this.loadPreviousDocumentTypes()
    },
  },
  created() {
    this.loadPreviousDocumentTypes()
    this.$http.get(`/v1/companies/${this.shipment.company_id}/warehouses`)
      .then(response => {
        const warehouses = response.data.data

        this.warehouses = this.warehouses.concat(warehouses.map(warehouse => ({ value: warehouse.id, text: warehouse.name })))
      })
    if (this.isPostValidationAmendment) {
      this.declaration.communication = {
        bank_account: this.shipment.company.bank_account,
        customs_attachments: [],
      }
    }
  },
  methods: {
    // Shared with every other classifier dropdown, so the categories read the
    // same way as the served lists next to them.
    formatOption: formatClassifierOption,
    // The flags (require_number_of_items) that drive the rule above. Needed only
    // for a selected type, and the dropdown showing the same list loads it for
    // the same value, so the two share one request through the shared cache.
    // A failure is not reported here: the dropdown owns the error state and its
    // Retry, and reloads this list along with its own.
    loadPreviousDocumentTypes() {
      const selected = this.declaration.previous_document_type
      if (selected === null || selected === undefined) {
        return
      }
      const country = this.previousDocumentCountry
      getPreviousDocumentTypes(country)
        .then(options => {
          if (this.previousDocumentCountry === country) this.previousDocumentTypes = options
        })
        .catch(() => {
          if (this.previousDocumentCountry === country) this.previousDocumentTypes = []
        })
    },
    submitFile() {
      const formData = new FormData()
      this.uploadFile.forEach((file, i) => {
        formData.append(`files[${i}]`, file)
      })
      this.upload.isUploading = true
      this.upload.uploadProgress = 0
      this.$http.post(`/v1/companies/${this.shipment.company_id}/shipments/${this.shipment.id}/declarations/${this.declaration.id}/files`,
        formData,
        {
          onUploadProgress: progressEvent => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total)
            this.upload.uploadProgress = percentCompleted

            return percentCompleted
          },
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })
        .then(response => {
          this.upload.isUploading = false
          if (response.data.customsAttachments) {
            this.declaration.communication.customs_attachments = response.data.customsAttachments
          }
          this.declaration.communication.files = response.data.files
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'File successfully uploaded',
              icon: 'EditIcon',
              variant: 'success',
            },
          })
        })
        .catch(() => {
          this.upload.isUploading = false
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Failed to upload file',
              icon: 'EditIcon',
              variant: 'danger',
            },
          })
        })
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
